import { z } from "zod";

import { projectRevisionSummarySchema } from "./projects";

// Traceability record of one persisted calculation run (Master Prompt v2.1
// item 15 / section 19). The backend keeps every module's runs in one generic
// table, so this summary is shared by all study pages and never belongs to a
// single module's service.
// The single-line diagram a run was checked against (SLD-b1, A27 (c), (d)): the pinned
// version and content hash and the outcome, read from the run's references snapshot; null
// for a run whose revision had no diagram, absent from older servers.
export const sldLinkWarningSchema = z
  .object({ code: z.string(), element: z.string(), message: z.string() })
  .strict();

export const sldLinkSchema = z
  .object({
    status: z.enum(["VALID", "REVIEW_REQUIRED"]),
    sld_version: z.number().int().positive(),
    sld_content_hash: z.string().length(64),
    warnings: z.array(sldLinkWarningSchema),
  })
  .strict();

export type SldLink = z.infer<typeof sldLinkSchema>;

// The stored fault run a cable study took its short-circuit current from (SLD-b3, A28 (e)):
// the run that gave the figure, the exact Ik'' the engine used, and the outcome, read from
// the run's references snapshot; null for a study that named no fault run, absent from
// older servers.
export const faultLinkWarningSchema = z
  .object({ code: z.string(), message: z.string() })
  .strict();

export const faultLinkSchema = z
  .object({
    status: z.enum(["VALID", "REVIEW_REQUIRED"]),
    fault_run_id: z.string().uuid(),
    calculation_key: z.string(),
    revision_number: z.number().int().positive(),
    fault_bus_code: z.string(),
    calculation_case: z.string(),
    fault_type: z.string(),
    prospective_current_ka: z.string(),
    warnings: z.array(faultLinkWarningSchema),
  })
  .strict();

export type FaultLink = z.infer<typeof faultLinkSchema>;

// The stored EOS-02 load run a power quality study took its demand figures from (EOS-11,
// A32): the run that gave them, the exact figures the engine used, and the outcome, read
// from the run's references snapshot; null for a study that named no load run, absent from
// older servers.
export const loadLinkWarningSchema = z
  .object({ code: z.string(), message: z.string() })
  .strict();

export const loadLinkSchema = z
  .object({
    status: z.enum(["VALID", "REVIEW_REQUIRED"]),
    load_run_id: z.string().uuid(),
    calculation_key: z.string(),
    revision_number: z.number().int().positive(),
    demand_power_kw: z.string(),
    apparent_power_kva: z.string(),
    reactive_power_kvar: z.string(),
    power_factor: z.string(),
    warnings: z.array(loadLinkWarningSchema),
  })
  .strict();

export type LoadLink = z.infer<typeof loadLinkSchema>;

// The stored EOS-06 cable runs the rows of an earth-fault loop study took their conductor
// figures from (EOS-05d, A33, D5), one per row keyed by the row code in the run's references
// snapshot (`cable_links`): the run and its revision, the figures as read, the run's own
// status and the outcome; absent when no row named a cable run.
export const cableLinkWarningSchema = z
  .object({ code: z.string(), message: z.string() })
  .strict();

export const cableLinkSchema = z
  .object({
    status: z.enum(["VALID", "REVIEW_REQUIRED"]),
    cable_run_id: z.string().uuid(),
    calculation_key: z.string(),
    revision_number: z.number().int().positive(),
    content_hash: z.string().length(64),
    run_status: z.string(),
    phase_area_mm2: z.string(),
    protective_area_mm2: z.string().nullable(),
    parallel_runs: z.number().int(),
    protective_conductor_type: z.string(),
    conductor_material: z.string(),
    insulation_material: z.string(),
    insulation_maximum_temperature_c: z.string(),
    route_length_m: z.string(),
    reactance_ohm_per_km: z.string().nullable(),
    warnings: z.array(cableLinkWarningSchema),
  })
  .strict();

// The stored EOS-06 cable run a row of a cable tray study was read from (EOS-12, A34): the run,
// the installation facts as read (construction, arrangement, method, grouping, parallel runs,
// phase area; no diameter and no mass - those are the row's declarations), the run's own status
// and the outcome. The same `cable_links` key, keyed by the row code.
export const trayCableLinkSchema = z
  .object({
    status: z.enum(["VALID", "REVIEW_REQUIRED"]),
    cable_run_id: z.string().uuid(),
    calculation_key: z.string(),
    revision_number: z.number().int().positive(),
    content_hash: z.string().length(64),
    run_status: z.string(),
    construction: z.string(),
    arrangement: z.string(),
    installation_method: z.string(),
    conductor_material: z.string(),
    insulation_material: z.string(),
    parallel_runs: z.number().int(),
    grouped_circuits: z.number().int().nullable(),
    phase_area_mm2: z.string().nullable(),
    warnings: z.array(cableLinkWarningSchema),
  })
  .strict();

// Each pin is exactly one of the two strict shapes; the panel tells them apart by the
// installation method only a tray pin carries.
export const anyCableLinkSchema = z.union([cableLinkSchema, trayCableLinkSchema]);
export const cableLinksSchema = z.record(z.string(), anyCableLinkSchema);

export type CableLink = z.infer<typeof cableLinkSchema>;
export type TrayCableLink = z.infer<typeof trayCableLinkSchema>;
export type AnyCableLink = z.infer<typeof anyCableLinkSchema>;
export type CableLinks = z.infer<typeof cableLinksSchema>;

// The stored EOS-04 earth-fault run a board conductor of an earthing study took its current
// from (EOS-08a, A36, D3), one per row keyed by the conductor row code in the run's references
// snapshot (`fault_links`): the shared fault-link shape with the exact earth-fault current the
// engine read beside the run's Ik''.
export const earthFaultLinkSchema = faultLinkSchema
  .extend({ earth_fault_current_ka: z.string().nullable() })
  .strict();
export const earthFaultLinksSchema = z.record(z.string(), earthFaultLinkSchema);

// The stored EOS-05d earth-fault loop row a circuit protective conductor of an earthing study
// took its time, current and area from (EOS-08a, A36, D5), one per row keyed by the conductor
// row code in the run's references snapshot (`disconnection_links`): the run and the row, the
// figures as read, the row's own status, whether the link closes that row's thermal-not-checked
// code, and the outcome.
export const disconnectionLinkWarningSchema = z
  .object({ code: z.string(), message: z.string() })
  .strict();

export const disconnectionLinkSchema = z
  .object({
    status: z.enum(["VALID", "REVIEW_REQUIRED"]),
    run_id: z.string().uuid(),
    calculation_key: z.string(),
    revision_number: z.number().int().positive(),
    content_hash: z.string().length(64),
    run_status: z.string(),
    bus_code: z.string(),
    row_code: z.string(),
    row_status: z.string(),
    disconnection_time_origin: z.string(),
    required_disconnection_time_s: z.string().nullable(),
    fault_current_a: z.string().nullable(),
    protective_conductor_area_mm2: z.string().nullable(),
    conductor_material: z.string().nullable(),
    disconnection_assured: z.boolean().nullable(),
    closes_thermal_not_checked: z.boolean(),
    warnings: z.array(disconnectionLinkWarningSchema),
  })
  .strict();
export const disconnectionLinksSchema = z.record(z.string(), disconnectionLinkSchema);

export type EarthFaultLink = z.infer<typeof earthFaultLinkSchema>;
export type EarthFaultLinks = z.infer<typeof earthFaultLinksSchema>;
export type DisconnectionLink = z.infer<typeof disconnectionLinkSchema>;
export type DisconnectionLinks = z.infer<typeof disconnectionLinksSchema>;

export const calculationRunSummarySchema = z
  .object({
    id: z.string().uuid(),
    // The project revision the run belongs to, and the names behind that id; both are null
    // for a run made outside any project (EOS-01 b).
    project_revision_id: z.string().uuid().nullable(),
    project: projectRevisionSummarySchema.nullable(),
    module_code: z.string(),
    calculation_type: z.string(),
    calculation_key: z.string(),
    revision_number: z.number().int(),
    run_status: z.string(),
    approval_status: z.string(),
    engine_version: z.string(),
    design_check_status: z.string(),
    jurisdiction_profile: z.string(),
    reference_verification_status: z.string(),
    content_hash: z.string().length(64),
    calculated_by: z.string().nullable(),
    calculated_at: z.string(),
    created_at: z.string(),
    is_immutable: z.boolean(),
    supersedes_run_id: z.string().uuid().nullable(),
    notes: z.string().nullable(),
    sld_link: sldLinkSchema.nullable().optional(),
    fault_link: faultLinkSchema.nullable().optional(),
    load_link: loadLinkSchema.nullable().optional(),
  })
  .strict();

export type CalculationRunSummary = z.infer<typeof calculationRunSummarySchema>;

// The frozen evidence of one run: the summary plus the four snapshots the backend stores
// (schemas/calculation_run.py, CalculationRunDetail). The snapshots are module-specific, so
// a study service narrows `result_snapshot` itself when it needs a field.
export const calculationRunDetailSchema = calculationRunSummarySchema
  .extend({
    input_snapshot: z.record(z.string(), z.unknown()),
    result_snapshot: z.record(z.string(), z.unknown()),
    warnings_snapshot: z.array(z.record(z.string(), z.unknown())),
    references_snapshot: z.record(z.string(), z.unknown()),
  })
  .strict();

// A page of run summaries, newest first (schemas/calculation_run.py,
// CalculationRunListResponse).
export const calculationRunListResponseSchema = z
  .object({
    items: z.array(calculationRunSummarySchema),
  })
  .strict();

export type CalculationRunDetail = z.infer<typeof calculationRunDetailSchema>;
export type CalculationRunListResponse = z.infer<typeof calculationRunListResponseSchema>;
